import React, { useEffect, useState } from 'react';
import { Plus, AlertTriangle } from 'lucide-react';
import Modal from './Modal';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';

const PHQ9_ITEMS = [
  'Little interest or pleasure in doing things',
  'Feeling down, depressed, or hopeless',
  'Trouble falling or staying asleep, or sleeping too much',
  'Feeling tired or having little energy',
  'Poor appetite or overeating',
  'Feeling bad about yourself — or that you are a failure or have let yourself or your family down',
  'Trouble concentrating on things, such as reading the newspaper or watching television',
  'Moving or speaking so slowly that other people could have noticed — or the opposite, being so fidgety or restless that you have been moving around a lot more than usual',
  'Thoughts that you would be better off dead or of hurting yourself in some way',
];
const OPTIONS = [
  { label: 'Not at all', value: 0 },
  { label: 'Several days', value: 1 },
  { label: 'More than half the days', value: 2 },
  { label: 'Nearly every day', value: 3 },
];
const IMPAIRMENT = [
  { label: 'Not difficult at all', value: 'none' },
  { label: 'Somewhat difficult', value: 'somewhat' },
  { label: 'Very difficult', value: 'very' },
  { label: 'Extremely difficult', value: 'extremely' },
];
const SEVERITY_LABEL = { minimal: 'Minimal depression', mild: 'Mild depression', moderate: 'Moderate depression', 'moderately severe': 'Moderately severe depression', severe: 'Severe depression' };

function severityKey(total) {
  if (total <= 4) return 'minimal';
  if (total <= 9) return 'mild';
  if (total <= 14) return 'moderate';
  if (total <= 19) return 'moderately severe';
  return 'severe';
}

// Baseline = earliest screening; change is per-screening vs. that baseline (no overwrite, full history).
function computeTrend(list) {
  const chronological = [...list].reverse(); // list is newest-first
  if (!chronological.length) return null;
  const baseline = chronological[0].total;
  return {
    baseline,
    points: chronological.map(s => ({ id: s.id, total: s.total, severity: s.severity, completed_at: s.completed_at, change_from_baseline: s.total - baseline })),
  };
}

export default function SectionPhq9({ patient, user, afterChange, notify, logDemo, updateDemoPatient, openSafety }) {
  const [screenings, setScreenings] = useState(patient.phq9 || []);
  const [composing, setComposing] = useState(false);
  const [responses, setResponses] = useState({});
  const [impairment, setImpairment] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [trend, setTrend] = useState(null);

  useEffect(() => {
    if (DEMO) { setScreenings(patient.phq9 || []); return; }
    api(`/patients/${patient.id}/phq9`).then(d => setScreenings(d.screenings || [])).catch(e => notify(e.message));
  }, [patient.id, patient.phq9]);

  useEffect(() => {
    if (DEMO) { setTrend(computeTrend(patient.phq9 || [])); return; }
    api(`/patients/${patient.id}/phq9/trending`).then(d => setTrend(d)).catch(e => notify(e.message));
  }, [patient.id, patient.phq9]);

  const total = PHQ9_ITEMS.reduce((n, _, i) => n + (responses[`q${i + 1}`] ?? 0), 0);
  const allScored = PHQ9_ITEMS.every((_, i) => responses[`q${i + 1}`] != null);

  function open() {
    setResponses({}); setImpairment(''); setNote(''); setComposing(true);
  }
  async function save() {
    setBusy(true);
    try {
      if (!allScored) throw new Error('Score all nine items before saving.');
      const body = { responses, functional_impairment: impairment || null, clinical_note: note || null };
      if (DEMO) {
        const entry = { id: `demo-phq9-${Date.now()}`, responses, total, severity: severityKey(total), functional_impairment: impairment || null, clinical_note: note || null, completed_by: user.id, completed_by_name: user.name, completed_at: new Date().toISOString() };
        const safety = (responses['q9'] ?? 0) > 0 ? { id: `demo-sa-${Date.now()}`, patient_id: patient.id, source_type: 'phq9', source_id: entry.id, trigger_kind: 'phq9_item9_positive', status: 'OPEN', priority: 'urgent', assigned_to: null, assignee_name: null, created_by: user.id, creator_name: user.name, created_at: new Date().toISOString(), completed_at: null, outcome: null, notes: null } : null;
        updateDemoPatient(patient.id, p => ({ phq9: [entry, ...(p.phq9 || [])], safety_assessments: safety ? [safety, ...(p.safety_assessments || [])] : (p.safety_assessments || []) }));
        setScreenings(s => [entry, ...s]);
        logDemo('phq9.completed', { patient_id: patient.id, total });
        if (safety) logDemo('phq9.safety_followup', { patient_id: patient.id });
      } else {
        await api(`/patients/${patient.id}/phq9`, { method: 'POST', body });
        const d = await api(`/patients/${patient.id}/phq9`);
        setScreenings(d.screenings || []);
      }
      setComposing(false);
      afterChange('PHQ-9 screening recorded.');
    } catch (e) { notify(e.message); }
    finally { setBusy(false); }
  }

  const latest = screenings[0];
  const openSafetyList = DEMO
    ? (patient.safety_assessments || []).filter(a => ['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'ESCALATED'].includes(a.status))
    : (patient.open_safety_assessments || []);

  return (
    <section className="content-card">
      <div className="content-heading">
        <div><h3>PHQ-9 Depression Screening</h3><p>Nine-item Patient Health Questionnaire · score 0–27.</p></div>
        {hasCap(user, 'chart.view') && <button className="button primary" onClick={open}><Plus size={15}/>New screening</button>}
      </div>

      {latest && (
        <div className={`phq9-latest ${['moderately severe', 'severe'].includes(latest.severity) ? 'phq9-flag' : ''}`}>
          <div className="phq9-score"><strong>{latest.total}</strong><span>/ 27</span></div>
          <div className="phq9-meta">
            <b>{SEVERITY_LABEL[latest.severity] || latest.severity}</b>
            <small>Most recent · {new Date(latest.completed_at).toLocaleString()} · {latest.completed_by_name || '—'}</small>
            {latest.functional_impairment && <small>Functional impact: {IMPAIRMENT.find(o => o.value === latest.functional_impairment)?.label || latest.functional_impairment}</small>}
          </div>
          {['moderately severe', 'severe'].includes(latest.severity) && (
            <div className="inline-alert danger" role="alert"><AlertTriangle size={14}/>Score is in the {latest.severity} range. Assess clinical risk and follow your facility’s suicide-prevention / crisis protocol as indicated.</div>
          )}
        </div>
      )}

      {openSafetyList.length > 0 && (
        <div className="inline-alert danger safety-alert" role="alert">
          <AlertTriangle size={16}/>
          <div>
            <strong>Safety follow-up open</strong>
            <p>A persistent safety follow-up is open for this patient (PHQ-9 Item 9 was positive). It does not resolve on its own — a clinician must act. The system does not infer risk level, PHP eligibility, hospitalization, or ED disposition.</p>
            {hasCap(user, 'safety.manage') && <button className="button" onClick={openSafety}>Start Safety Assessment</button>}
          </div>
        </div>
      )}

      {trend && trend.points.length > 1 && (
        <div className="phq9-trend">
          <div className="phq9-trend-head"><b>Trend</b><small>Change vs. baseline ({trend.baseline})</small></div>
          <div className="phq9-trend-chart">
            {trend.points.map(p => {
              const delta = p.change_from_baseline;
              return (
                <div className="phq9-trend-col" key={p.id} title={`${p.total}/27 · ${new Date(p.completed_at).toLocaleString()}`}>
                  <span className="phq9-trend-score">{p.total}</span>
                  <div className="phq9-trend-bar-wrap"><div className="phq9-trend-bar" style={{ height: `${Math.max(4, (p.total / 27) * 100)}%` }} /></div>
                  <span className={`phq9-trend-delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : ''}`}>{delta > 0 ? `+${delta}` : delta}</span>
                  <span className="phq9-trend-date">{new Date(p.completed_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="table-scroll">
        <table>
          <thead><tr><th>Completed</th><th>Score</th><th>Severity</th><th>Functional impact</th><th>By</th><th>Note</th></tr></thead>
          <tbody>
            {screenings.map(s => (
              <tr key={s.id}>
                <td>{new Date(s.completed_at).toLocaleString()}</td>
                <td><strong>{s.total}</strong></td>
                <td><span className={`badge ${['moderately severe', 'severe'].includes(s.severity) ? 'red' : 'neutral'}`}>{SEVERITY_LABEL[s.severity] || s.severity}</span></td>
                <td>{s.functional_impairment ? IMPAIRMENT.find(o => o.value === s.functional_impairment)?.label || s.functional_impairment : '—'}</td>
                <td>{s.completed_by_name || '—'}</td>
                <td>{s.clinical_note || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!screenings.length && <p className="empty-copy">No PHQ-9 screenings recorded. Over the last two weeks, how often has the patient been bothered by the problems below?</p>}
      </div>

      {composing && (
        <Modal title="New PHQ-9 Screening" onClose={() => setComposing(false)} wide>
          <p className="phq9-intro">Over the last two weeks, how often has the patient been bothered by any of the following problems?</p>
          <div className="phq9-form">
            {PHQ9_ITEMS.map((item, i) => (
              <div className="phq9-row" key={i}>
                <div className="phq9-q"><b>{i + 1}.</b> {item}{i === 8 && <span className="phq9-q9-tag">Item 9 — self-harm</span>}</div>
                <div className="phq9-opts">
                  {OPTIONS.map(o => (
                    <label key={o.value} className="phq9-opt">
                      <input type="radio" name={`phq9-q${i + 1}`} checked={responses[`q${i + 1}`] === o.value} onChange={() => setResponses(r => ({ ...r, [`q${i + 1}`]: o.value }))} />
                      <span>{o.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="phq9-total">
            <span>Total score</span>
            <strong>{total}</strong><span>/ 27</span>
            <span className={`badge ${['moderately severe', 'severe'].includes(severityKey(total)) ? 'red' : 'neutral'}`}>{SEVERITY_LABEL[severityKey(total)]}</span>
          </div>

          <label className="phq9-impairment-label">If any problems were checked, how difficult have these problems made it to work, take care of things at home, or get along with others?</label>
          <select className="phq9-impairment" value={impairment} onChange={e => setImpairment(e.target.value)}>
            <option value="">— Select —</option>
            {IMPAIRMENT.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <label className="modal-label">Clinical note (optional)<textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Interpretation, risk assessment, plan…" maxLength={4000}/></label>

          {responses['q9'] != null && responses['q9'] > 0 && (
            <div className="inline-alert danger" role="alert"><AlertTriangle size={14}/>Item 9 indicates thoughts of self-harm. Saving opens a persistent safety follow-up; follow your facility’s crisis protocol and complete the safety assessment. The system does not infer risk level, PHP eligibility, hospitalization, or ED disposition.</div>
          )}

          <div className="modal-actions">
            <button className="button" onClick={() => setComposing(false)}>Cancel</button>
            <button className="button primary" disabled={busy || !allScored} onClick={save}>{busy ? 'Saving…' : 'Save screening'}</button>
          </div>
        </Modal>
      )}
    </section>
  );
}
